import { OverlayProvider } from '@react-aria/overlays';
import { IntersectionObserverContextProvider, LazyLoadContextProvider } from '@riac/core';
import { LinkContext, ModalScrollContextProvider, Spinner } from '@riac/design-system';
import Cookies from 'js-cookie';
import React, { Suspense, useContext, useState } from 'react';
import { Switch } from 'react-router';
import { BrowserRouter, Redirect, Route, StaticRouter } from 'react-router-dom';
import { CrispChat } from 'src/components/CrispChat';
import { Footer } from 'src/components/Footer/Footer';
import { Header } from 'src/components/Header/Header';
import { ScrollToTop } from 'src/components/ScrollToTop';
import { ServerSideRenderingError } from 'src/components/ServerSideRenderingError';
import { YouHaveBeenLoggedOut } from 'src/components/YouHaveBeenLoggedOut';
import { AccountHeaderContextProvider } from 'src/lib/AccountHeaderContext';
import { AnalyticsContextProvider } from 'src/lib/AnalyticsContext';

// import { CampaignContextProvider } from 'src/lib/CampaignContext';
import { CatalogHistoryContextProvider } from 'src/lib/CatalogHistoryContext';
import ErrorBoundary from 'src/lib/ErrorBoundary';
import { GlobalBannerContextProvider } from 'src/lib/GlobalBannerContext';
import { LoginContextProvider } from 'src/lib/LoginContext';
import { PageLoader } from 'src/lib/PageLoader';
import { ScrollContextProvider } from 'src/lib/ScrollContext';
import { SocketContext } from 'src/lib/SocketContext';
import { SsrInitDataContext } from 'src/lib/SsrInitDataContext';
import { UserItemContextProvider } from 'src/lib/UserItemContext';
import { useSocketEvent } from 'src/lib/useSocketEvent';
import { WishlistActionContextProvider } from 'src/lib/WishlistActionContext';
import { LoadableErrorBoundary } from 'src/LoadableErrorBoundary';
import { AsyncAboutUsPage } from 'src/pages/AboutUsPage/AsyncAboutUsPage';
import { AsyncAdCampaignsPage } from 'src/pages/AdCampaigns/AsyncAdCampaignsPage';
import { AsyncAdCampaignsSinglePage } from 'src/pages/AdCampaigns/AsyncAdCampaignsSinglePage';
import { AsyncAuctionsPage } from 'src/pages/AuctionsPage/AsyncAuctionsPage';
import { AsyncAuctionsSinglePage } from 'src/pages/AuctionsSinglePage/AsyncAuctionsSinglePage';
import { AsyncCareersPage } from 'src/pages/CareersPage/AsyncCareersPage';
import { AsyncCatalogPage } from 'src/pages/CatalogPage/AsyncCatalogPage';
import { AsyncCatalogLandingPage } from 'src/pages/CatalogPage/CatalogLandingPage/AsyncCatalogLandingPage';
import landingPages from 'src/pages/CatalogPage/landing-pages';
import { AsyncConsignmentGettingStartedPage } from 'src/pages/ConsignmentGettingStartedPage/AsyncConsignmentGettingStartedPage';
import {
  AsyncConsignmentNotableCollectorsPage,
} from 'src/pages/ConsignmentNotableCollectors/AsyncConsignmentNotableCollectorsPage';
import {
  AsyncConsignmentSingleCollectorPage,
} from 'src/pages/ConsignmentNotableCollectors/AsyncConsignmentSingleCollectorPage';
import { AsyncConsignmentPage } from 'src/pages/ConsignmentPage/AsyncConsignmentPage';
import { AsyncContactPage } from 'src/pages/ContactPage/AsyncContactPage';
import { AsyncCreateAccountPage } from 'src/pages/CreateAccountPage/AsyncCreateAccountPage';
import { AsyncDetailPage } from 'src/pages/DetailPage/AsyncDetailPage';
import { AsyncFAQPage } from 'src/pages/FAQPage/AsyncFAQPage';
import { AsyncFAQSinglePage } from 'src/pages/FAQPage/FAQSinglePage/AsyncFAQSinglePage';
import { GhostPages } from 'src/pages/GhostPages';
import { AsyncGhostPostSinglePage } from 'src/pages/GhostPostSinglePage/AsyncGhostPostSinglePage';
import { AsyncLocationBedfordPage } from 'src/pages/LocationsPage/LocationBedfordPage/AsyncLocationBedfordPage';
import MaintenancePage from 'src/pages/MaintenancePage/MaintenancePage';
import { AsyncNewsAndEventsPage } from 'src/pages/NewsAndEventsPage/AsyncNewsAndEventsPage';
import { Page404 } from 'src/pages/Page404';
import './global.css';
import { AgeVerificationCheck } from './components/AgeVerificationCheck/AgeVerificationCheck';
import { GlobalDataContextProvider } from './lib/GlobalDataContext';
import { AsyncConsignmentPastSuccessPage } from './pages/ConsignmentPastSuccessPage/AsyncConsignmentPastSuccessPage';
import { AsyncConsignmentPastThatDrivesUsPage } from './pages/ConsignmentPastThatDrivesUsPage/AsyncConsignmentPastThatDrivesUsPage';
import { AsyncConsignmentProcessPage } from './pages/ConsignmentProcessPage/AsyncConsignmentProcessPage';
import { AsyncIconGalleryPage } from './pages/dev/AsyncIconGalleryPage';
import { AsyncToppleTriggers } from 'src/pages/dev/AsyncToppleTriggers';
import { AsyncLiveFallbackPage } from './pages/LiveFallbackPage/AsyncLiveFallbackPage';

const AsyncAccountPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/AccountPage'
  componentImport={async () => (await import('src/pages/AccountPage/AccountPage'))}
  {...props}/>;
const AsyncOrderCatalogPage = ({ ...props }) => <PageLoader
  id='src/pages/OrderCatalogPage/OrderCatalogPage'
  componentImport={() => import('src/pages/OrderCatalogPage/OrderCatalogPage') as any}
  {...props}/>;

const AsyncFrontPage = ({ ...props }) => <PageLoader
  id='src/pages/FrontPage/FrontPage'
  componentImport={() => import('src/pages/FrontPage/FrontPage')}
  {...props}/>;
const AsyncTermsAndConditionsPage = ({ ...props }) => <PageLoader
  id='src/pages/TermsAndConditionsPage'
  componentImport={() => import('src/pages/TermsAndConditionsPage')}
  {...props}/>;
const AsyncLoginPage = ({ ...props }) => <PageLoader
  id='src/pages/LoginPage'
  componentImport={() => import('src/pages/LoginPage')}
  {...props}/>;
const AsyncPasswordResetPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/PasswordReset/PasswordReset'
  componentImport={() => import('src/pages/AccountPage/PasswordReset/PasswordReset')}
  {...props}/>;
const AsyncPasswordResetPageWithCode = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/PasswordReset/PasswordResetWithCode'
  componentImport={() => import('src/pages/AccountPage/PasswordReset/PasswordResetWithCode')}
  {...props}/>;
const AsyncWishlistImport = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/WishlistImport/WishlistImport'
  componentImport={() => import('src/pages/AccountPage/WishlistImport/WishlistImport')}
  {...props}/>;
const AsyncAccountInfoValidatePage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/AccountInfoValidatePage'
  componentImport={() => import('src/pages/AccountPage/AccountInfoValidatePage')}
  {...props}/>;
const AsyncEmailLinkVerifyPage = ({ ...props }) => <PageLoader
  id='src/pages/AccountPage/EmailLinkVerifyPage'
  componentImport={() => import('src/pages/EmailLinkVerifyPage/EmailLinkVerifyPage')}
  {...props}/>;

const Providers = ({ children, hashMatch }: React.PropsWithChildren<{ hashMatch: boolean }>): JSX.Element => (
  <OverlayProvider>  {/* React-Aria */}
    <LinkContext.Provider value={{ domainName: window.DOMAIN_NAME, hashMatch: hashMatch }}>
      <LoginContextProvider>
        <GlobalDataContextProvider>
          <AccountHeaderContextProvider>
            <UserItemContextProvider>
              <ModalScrollContextProvider>
                <ScrollContextProvider>
                  <IntersectionObserverContextProvider>
                    <LazyLoadContextProvider>
                      <GlobalBannerContextProvider>
                        <AnalyticsContextProvider>
                          {children}
                        </AnalyticsContextProvider>
                      </GlobalBannerContextProvider>
                    </LazyLoadContextProvider>
                  </IntersectionObserverContextProvider>
                </ScrollContextProvider>
              </ModalScrollContextProvider>
            </UserItemContextProvider>
          </AccountHeaderContextProvider>
        </GlobalDataContextProvider>
      </LoginContextProvider>
    </LinkContext.Provider>
  </OverlayProvider>
);

export const MaintenanceModeRoutes = (): JSX.Element => {
  return <Switch>
    <Route path='/letmein' component={() => {
      Cookies.set('skipMaintenance', true as any); // This should probably be a string but i don't want to touch it and it works.
      if(window && window.location && window.location.pathname) window.location.pathname = '/';
      return (
        <div>Letting you in...</div>
      );
    }}/>
    <Route path='/' render={() => {
      return <MaintenancePage/>;
    }}/>
  </Switch>;
};

export const AppRoutes = (): JSX.Element => {
  return <Route path='/:p0?/:p1?/:p2?/:p3?/:p4?/:p5?' render={({ match }) => (
    <LoadableErrorBoundary onError={(error, info) => console.error(error, info)}>
      <Switch>
        {/*
          * Items have attached QR codes that the client can scan
          * at home once they received their won item.  The QR Code
          * encodes http://www.rockislandauction.com/qr/cs/:auctionId/:lotNumber
          * This endpoint is a hook to redirect them, as where we want these
          * links to lead may change over time
        */}
        <Route path='/qr/cs/' render={({ location }) => {
          return <Redirect to={'/detail' + location.pathname.slice(6)}/>;
        }}/>
        <Route path='/dev/icon-gallery' render={() => <AsyncIconGalleryPage/>}/>
        {/* <Route exact path='/whiskey-and-war-stories' render={() => <AsyncWhiskeyAndWarStoriesPage/>}/> */}
        <Route path='/pricesrealized'>
          <Redirect to='/gun-auctions'/>
        </Route>
        <Route exact path='/about-us'
          render={() => <AsyncAboutUsPage/>}
        />
        <Route exact path='/gun-auctions'
          render={() => <AsyncAuctionsPage/>}
        />
        <Route exact path='/category' render={() => <Redirect to='/catalog'/>}/>
        <Route
          exact
          path='/(catalog|category)/deringer'
          render={() => <Redirect to='/category/derringer'/>}
        />
        <Route exact path='/faq'
          render={() => <AsyncFAQPage/>}
        />
        <Route exact path='/riac-blog'
          render={() => <Redirect to='/news-and-events'/>}
        />
        <Route exact path='/news'
          render={() => <Redirect to='/news-and-events'/>}
        />
        <Route exact path='/live'
          render={() => <AsyncLiveFallbackPage/>}
        />
        <Route exact path='/login' render={() => <AsyncLoginPage/>}/>
        <Route exact path='/login/password-reset' render={() => <AsyncPasswordResetPage/>}/>
        <Route exact path='/login/password-reset/:code' render={() => <AsyncPasswordResetPageWithCode/>}/>
        <Route exact path='/email/linkVerify/:code' render={() => <AsyncEmailLinkVerifyPage/>}/>
        <Route exact path='/create-account/:code?' render={() => <AsyncCreateAccountPage/>}/>
        <Route exact path='/past-auction-successes'
          render={() => <Redirect to='/consignment/past-auction-successes'/>}
        />
        <Route exact path='/consignment/past-auction-successes' render={() => <AsyncConsignmentPastSuccessPage/>}/>
        <Route exact path='/consignment/notable-collectors' render={() => <AsyncConsignmentNotableCollectorsPage/>}/>
        <Route exact path='/consignment/consignment-process'
          render={() => <AsyncConsignmentProcessPage/>}
        />
        <Route exact path='/consignment/the-past-that-drives-us'
          render={() => <AsyncConsignmentPastThatDrivesUsPage/>}
        />
        <Route exact path={'/topple_test_page'} render={() => <AsyncToppleTriggers />} />
        <Route exact path='/'
          render={() => <AsyncFrontPage/>}
        />
        <Route path='/(catalog|detail|category)'>
          <WishlistActionContextProvider>
            <CatalogHistoryContextProvider>
              <Switch>
                <Route path='/(catalog|category)'>
                  <Switch>
                    {landingPages.map((landingPage) => {
                      return (
                        <Route
                          key={landingPage.slug}
                          exact
                          path={`/(catalog|category)/${landingPage.slug}`}
                          render={() => <AsyncCatalogLandingPage basicLandingPage={landingPage}/>}
                        />
                      );
                    })}
                    <Route>
                      <Switch>
                        <Route path='/catalog/:p0?/:p1?/:p2?/:p3?/:p4?/:p5?'>
                          <AsyncCatalogPage/>
                        </Route>
                        <Page404/>
                      </Switch>
                    </Route>
                  </Switch>
                </Route>
                <Route exact path='/detail/:auctionid/:lotnumber'>
                  <AsyncDetailPage/>
                </Route>
                <Route exact path='/detail/:auctionid/:lotnumber/:slug'>
                  <AsyncDetailPage/>
                </Route>
                <Page404/>
              </Switch>
            </CatalogHistoryContextProvider>
          </WishlistActionContextProvider>
        </Route>
        <Route path='/account/validate/:type?/:success?/:code?'
          render={({ match }) => <AsyncAccountInfoValidatePage match={match}/>}
        />
        <Route path={'/account/wishlist/shared/:wlid?'}
          render={(match) => (
            <WishlistActionContextProvider>
              <AsyncWishlistImport {...match}/>
            </WishlistActionContextProvider>
          )}
        />
        <Route path='/account'
          render={() => <AsyncAccountPage/>}
        />
        <Route path='/news-and-events/:page?'
          render={() => <AsyncNewsAndEventsPage/>}
        />
        <GhostPages match={match}>
          <Switch>
            <Route path='/order-catalog'
              component={({ match }) => <AsyncOrderCatalogPage match={match}/>}
            />
            <Route exact path='/careers'
              component={() => <AsyncCareersPage/>}
            />
            <Route exact path='/faq/:slug'
              render={(match) => <AsyncFAQSinglePage {...match}/>}
            />
            <Route exact path='/riac-blog/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/news/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/video/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/events/:title'
              render={(match) => <AsyncGhostPostSinglePage {...match}/>}
            />
            <Route exact path='/adcampaign'
              render={() => <AsyncAdCampaignsPage/>}
            />
            <Route exact path='/adcampaign/:slug'
              render={() => <AsyncAdCampaignsSinglePage/>}
            />
            <Route exact path='/consignment/getting-started'
              render={(match) => <AsyncConsignmentGettingStartedPage {...match}/>}
            />
            <Route exact path='/terms-and-conditions'
              render={() => <AsyncTermsAndConditionsPage/>}
            />
            <Route path='/location/bedford'
              render={() => <AsyncLocationBedfordPage/>}
            />
            <Route path='/gun-auctions/:slug'
              render={(match) => <AsyncAuctionsSinglePage {...match}/>}
            />
            <Route exact path='/consignment'
              render={() => <AsyncConsignmentPage/>}
            />
            <Route exact path='/consignment/who-we-are'
              render={() => <Redirect to='/consignment'/>}
            />
            <Route exact path='/consignment/how-much-is-your-gun-worth/:guntype?/:mfg?/:model?'
              render={() => <Redirect to='/consignment'/>/* <AsyncGunPriceEstimatorPage {...match}/>*/}
            />
            <Route exact path='/about-us'
              render={() => <AsyncAboutUsPage/>}
            />
            <Route exact path='/contact' render={() => <AsyncContactPage/>}/>
            <Route exact path='/consignment/notable-collectors/:slug' render={() => <AsyncConsignmentSingleCollectorPage/>}/>
            <Route render={() => <Page404/>}/>
          </Switch>
        </GhostPages>
      </Switch>
    </LoadableErrorBoundary>
  )}/>;
};

export const App = (): JSX.Element => {
  const [hashMatch, setHashMatch] = useState<boolean>(true);
  const { location, ssr, skipMaintenance } = useContext(SsrInitDataContext);
  const Router: any = (ssr && StaticRouter) || BrowserRouter; // TODO: Bad any but they don't provide a better type
  const { socketHookManager } = useContext(SocketContext);

  useSocketEvent(socketHookManager, 'hashcheck', (msg) => {
    console.log('socket hashcheck', { current: VERSION_HASH, latest: msg.hash, match: VERSION_HASH === msg.hash });
    setHashMatch(VERSION_HASH === msg.hash);
  });

  if(MAINTENANCE_MODE == 'true' && (!skipMaintenance && !Cookies.get('skipMaintenance'))) {
    return <>
      <Providers hashMatch = { hashMatch }>
        <Router
          context={{}}
          location={location && location.href}
        >
          <ErrorBoundary>
            <div className='flex min-h-[101vh] flex-col print:min-h-0'>
              <ScrollToTop/>
              <MaintenanceModeRoutes/>
            </div>
          </ErrorBoundary>
        </Router>
      </Providers>
    </>;
  }

  return (<>
    <Providers hashMatch = { hashMatch }>
      <Router
        context={{}}
        location={location && location.href}
      >
        <div className='flex min-h-[101vh] flex-col print:min-h-0'>
          <ServerSideRenderingError/>
          <div>
            <ErrorBoundary>
              <Suspense>
                <YouHaveBeenLoggedOut/>
                <ScrollToTop/>
                <AgeVerificationCheck/>
                <Header/>
                <Suspense fallback={<Spinner/>}>
                  <div>
                    <AppRoutes/>
                  </div>
                  <div className='flex grow-[10000]'/>
                </Suspense>
                <Footer/>
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </Router>
      <CrispChat/>
    </Providers>
  </>
  );
};
